import CloseIcon from '@mui/icons-material/Close';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Button,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';
import { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import EmploymentSubMenu from '@/components/nav/EmploymentSubMenu';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import ExitPopup940 from '@/components/ui/ExitPopup940';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';
import { getLastDateOfTheMonth } from '@/utils/utils';

const Form940 = () => {
  const [open, setOpen] = useState(false);
  const [closeSticky, setCloseSticky] = useState<boolean>(false);
  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Simplify your annual <span style="color:#1da94a; font-weight: 700;">unemployment tax</span> filings`,
  //   SubHeading:
  //     'E-file <span style="color:#1da94a; font-weight: 700;"> Form 940</span> today to stay ahead and ensure timely filing.',
  //   CtaText: 'E-file Form 940 Now!',
  //   CtaSubHeading: ``,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940sticky`,
  //   isActive: true,
  // };

  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());
  const handleStickyClose = (): void => {
    setCloseSticky(true);
  };

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 2, 2));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 2, 2)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);
  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1,
            pt: 0.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 60, md: 60 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 900,
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.2 },
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 10 },
            fontWeight: 500,
            color: '#ccc',
            mt: 0,
            mx: -1,
            mb: -1,
            py: 0.2,
            px: { xs: 0, sm: 0, lg: 0 },
            borderRadius: '0px 0px 8px 8px',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Day</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };

  return (
    <AppLayout
      // LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="E-file IRS Form 940 (FUTA) for the Tax Year 2025 "
          description="File IRS Form 940 online for 2025 and simplify your FUTA compliance. Enjoy fast, error-free, and secure e-filing for your federal unemployment tax return."
          keywords=""
          canonical="employment-tax/e-file-irs-form-940/"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
          ogShareImg="assets/images/employment-share-img.png"
        />
      }
    >
      <Hidden smDown>
        {!closeSticky && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              left: 0,
              right: 0,
              zIndex: 3,
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                backgroundColor: '#fff',
                mx: { xs: 3, lg: 'auto' },
                width: 'auto',
                py: 1.5,
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                boxShadow: '0 0 20px rgba(0,0,0,.5)',
                position: 'relative',
                borderWidth: 2,
                borderColor: '#414042',
                borderStyle: 'solid',
                borderBottom: 'none',
              }}
            >
              <Tooltip title="Close" arrow placement="top">
                <IconButton
                  aria-label="close"
                  size="small"
                  onClick={handleStickyClose}
                  sx={{
                    position: 'absolute',
                    right: -12,
                    top: -12,
                    color: '#fff',
                    backgroundColor: '#e11d1d',
                    '&:hover, &:focus': {
                      backgroundColor: '#b01919',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 1, sm: 1, md: 2 }}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <Typography
                    component="div"
                    variant="body1"
                    sx={{
                      textAlign: { xs: 'center', sm: 'left' },
                      fontSize: { xs: 15, sm: 17, md: 18, lg: '1.25rem' },
                      //  color: '#ccc',
                      lineHeight: 1.3,
                    }}
                  >
                    <strong>📢 Missed the Feb 2 deadline! </strong> E-file your{' '}
                    <b style={{ fontWeight: 600, color: '#213e99' }}>
                      2025 Form 940
                    </b>{' '}
                    now to minimize IRS penalties.
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Countdown date={lastDayTaxSeason} renderer={renderer} />
                  <Hidden mdUp>
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        size="medium"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form940`}
                        sx={{
                          textTransform: 'none',
                          px: 2.5,
                          display: 'inline-block',
                          boxShadow: 'none',
                          borderRadius: 4.2,
                          backgroundColor: '#213e99',
                          whiteSpace: 'nowrap',
                          '&:hover, &:focus': {
                            backgroundColor: '#172b6b',
                          },
                        }}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Hidden>
                </Stack>
                <Hidden mdDown>
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="medium"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form940`}
                      sx={{
                        textTransform: 'none',
                        px: 2.5,
                        display: 'inline-block',
                        boxShadow: 'none',
                        borderRadius: 4.2,
                        backgroundColor: '#213e99',
                        whiteSpace: 'nowrap',
                        '&:hover, &:focus': {
                          backgroundColor: '#172b6b',
                        },
                      }}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Hidden>
              </Stack>
            </Container>
          </Box>
        )}
      </Hidden>
      <ExitPopup940 />
      <EmploymentSubMenu />
      <ComingSoonPopup open={open} setOpen={setOpen} />
      {/* <Container maxWidth="md">
        <Box sx={{ px: 2, mt: 3, mx: 0 }}>
          <Alert
            severity="warning"
            icon={<WarningIcon sx={{ fontSize: 30 }} />}
            sx={{ borderRadius: 3 }}
          >
            <AlertTitle
              sx={{ fontSize: { xs: 18, sm: 20, md: 22 }, fontWeight: 600 }}
            >
              Attention! Last Day to E-File Forms 940!
            </AlertTitle>

            <Typography
              variant="body1"
              sx={{ fontSize: { xs: 16, sm: 17, md: 18 } }}
            >
              Don&apos;t miss the deadline! Today is the <b>final day</b> to
              electronically file your <b>Form 940</b> (Annual Federal
              Unemployment Tax Return).
            </Typography>
          </Alert>
        </Box>
      </Container> */}
      <Stack
        component="section"
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          position: { xs: 'relative', sm: 'sticky' },
          background: 'linear-gradient(135deg, #0a7581, #0d2b2f)',
          p: 1.5,
          px: 2.5,
          color: '#fff',
          minHeight: 40,
          left: 0,
          right: 0,
          zIndex: 11,
          textAlign: 'center',
          top: { xs: 0, sm: 114, md: 118 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#e1e1e1',
            lineHeight: 1.3,
            fontSize: { xs: 14, sm: 16, md: 18, lg: 20 },
          }}
        >
          ⚠️The deadline has passed. E-file your{' '}
          <span style={{ fontWeight: 600, color: '#ecf39e' }}>
            W-2, 1099-NEC & 94X forms
          </span>{' '}
          now to minimize IRS penalties.
        </Typography>
        <Button
          variant="contained"
          size="small"
          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form940`}
          sx={{
            backgroundColor: '#fff',
            '&:hover, &:focus': {
              backgroundColor: '#0a7581',
              color: '#fff',
              // backgroundColor: '#213e99',
              // color: '#fff',
            },
            minWidth: 180,
            px: 2,
            py: 0.5,
            color: '#333',
            mt: { xs: 0, md: 0 },
            display: { xs: 'inline-block', sm: 'inline-block' },
            mx: { xs: 'auto', sm: 1 },
            borderRadius: 2,
            boxShadow: 'none',
            fontSize: { xs: 12, sm: 14, md: 16 },
            textTransform: 'none',
          }}
        >
          E-file Form 940 Now
        </Button>
      </Stack>

      <Container
        maxWidth="xl"
        component="section"
        sx={{ mt: { sm: 3, md: 3 } }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-file IRS{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 940</span>
                </span>{' '}
                for the 2025 tax year
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                File the annual FUTA tax return online with TaxZerone
                effortlessly and efficiently. Complete your Form 940 filing and
                stay tax compliant.
              </Typography>

              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mr: { xs: 0, md: 4 },
                    mb: { xs: 2, sm: 0 },
                  }}
                >
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form940`}
                  >
                    E-file Form 940
                  </Button>
                  {/* <Typography
                    variant="body2"
                    sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
                  >
                    Takes 3 steps and less than 5 minutes
                  </Typography> */}
                </Box>
                <Hidden mdDown>
                  <Box sx={{}}>
                    <picture>
                      <source
                        srcSet="/assets/images/irs-authorized.webp"
                        className="mw-100"
                        width={70}
                        height={69}
                      />
                      <source
                        srcSet="/assets/images/irs-authorized.png"
                        className="mw-100"
                        width={70}
                        height={69}
                      />
                      <img
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        className="mw-100"
                        width={70}
                        height={69}
                      />
                    </picture>
                  </Box>
                </Hidden>
              </Stack>
              <Box
                sx={{
                  position: 'relative',
                  maxWidth: 580,
                  mx: { xs: 'auto', md: 0 },
                  mt: 5,
                }}
              >
                {/* <Box
                  sx={{
                    background: '#333',
                    p: 1,
                    px: 2,
                    color: '#fff',
                    '&.ribbon:before': { left: 0 },
                    '&.ribbon:after': { right: 0 },
                  }}
                  className="ribbon"
                >
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: { xs: 'center', sm: 'center' },
                        fontSize: { xs: 16, sm: 18, md: 20 },
                      }}
                    >
                      Form 941 for Quarter 3, 2023, is due on{' '}
                      <strong>October 31</strong>
                    </Typography>
                  </Box>
                </Box> */}
              </Box>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/940-landing-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/940-landing-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/940-landing-img.png"
                  alt="E-file IRS Form 940 for the 2025 tax year"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/940-landing-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/940-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/940-landing-img-sm.png"
                  alt="E-file IRS Form 940 for the 2025 tax year"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 30, sm: 26, xs: 22 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                E-file Form 940 at the best price in the industry
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                }}
              >
                Our flat pricing offers you exceptional value. This means what
                you see listed is what you can expect to pay—not a cent more.
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 250, md: 280 },
                textAlign: 'center',
                fontSize: 18,
                lineHeight: 1.2,
              }}
            >
              Get your return filed at just
              <br />
              <Box
                component="strong"
                sx={{
                  color: '#ffe400',
                  fontSize: { xs: 30, sm: 36, md: 48, lg: 60 },
                }}
              >
                $6.99
              </Box>
              <Typography
                variant="body2"
                sx={{ mt: 0, fontSize: { xs: 11, sm: 14 } }}
              >
                Flat fee. No hidden charges.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                File Form 940 for your business with the IRS in just 3 simple
                steps
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Follow the steps below to report FUTA taxes for the tax year.
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source
                  srcSet="/assets/images/940-steps-img.webp"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <source
                  srcSet="/assets/images/940-steps-img.png"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <img
                  src="/assets/images/940-steps-img.png"
                  alt="File Form 940 for your business with the IRS in just 3 simple steps"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Box
                component="ol"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  p: 2,
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter Form 940 information"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter Form 940 information
                    </Typography>
                    <Typography variant="body1">
                      Provide the business information along with payment made
                      to employees and FUTA taxes applicable.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Pay the balance due and sign Form 940"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Pay the balance due and sign Form 940
                    </Typography>
                    <Typography variant="body1">
                      Select the payment method that suits you to pay the FUTA
                      balance due and e-sign the return.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Transmit the return to the IRS"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Transmit the return to the IRS
                    </Typography>
                    <Typography variant="body1">
                      Make sure the information provided is accurate, and
                      transmit your 940 return to the IRS.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Box>

              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form940`}
                >
                  E-file Form 940
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: { xs: 2, sm: 2, md: 5 } }}
                >
                  Get notified as soon as your return is accepted.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
        component="section"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Top reasons to choose TaxZerone for Form 940 e-filing
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Here&apos;s how TaxZerone simplifies Form 940 filing for your
                business.
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="IRS-authorized"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  IRS-authorized
                </Typography>
                <Typography variant="body1">
                  Your tax filings are processed securely and in compliance with
                  all regulations. Confidently e-file your Form 940, knowing
                  that it will be accepted and processed by the IRS without any
                  issues.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/smart-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/smart-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/smart-icon.png"
                      alt="business validations"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Built-in business validation
                </Typography>
                <Typography variant="body1">
                  Get benefitted from our in-built smart validations acting as
                  your virtual tax assistant. These checks not only save time
                  but also minimize errors and ensure precise and error-free
                  submissions for your peace of mind.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/time-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/time-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/time-icon.png"
                      alt="Real time update"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Real-time updates
                </Typography>
                <Typography variant="body1">
                  Stay informed with instant notifications about your
                  filing&apos;s status. Get notified instantly as soon as the
                  IRS processes your 940 return. Also, track the status from
                  your dashboard.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/lowest-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/lowest-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/lowest-icon.png"
                      alt="competitive price"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Competitive pricing
                </Typography>
                <Typography variant="body1">
                  File your Form 940 at the most competitive rates without
                  compromise. No hidden fees or unexpected charges. Get
                  top-notch, cost-effective solutions for your tax filing needs.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/sign-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/sign-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/sign-icon.png"
                      alt="E-sign form 940"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  E-sign Form 940
                </Typography>
                <Typography variant="body1">
                  Complete your 940 filing swiftly using{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-94x-pin-request/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    94x online signature PIN
                  </Link>
                  . Don&apos;t you have an online signature PIN? Use Form
                  8453-EMP to e-sign your return and transmit it to the IRS
                  securely.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/easy-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/easy-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/easy-icon.png"
                      alt="e-filing simplified"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  E-filing simplified
                </Typography>
                <Typography variant="body1">
                  Experience a user-friendly and streamlined process. Our
                  intuitive interface ensures quick completion of the filing
                  process in just minutes. Enjoy a stress-free tax filing
                  experience that saves you time and effort.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent={{ xs: 'center', md: 'center' }}
              >
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-1"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-2"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-3"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-4"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-5"
                />
              </Stack>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 2 },
                }}
              >
                See How Tax Filing Became Easier for Business Owners
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                See why businesses trust TaxZerone for smooth, simple, and
                reliable tax filing.
              </Typography>
              <Grid
                container
                spacing={2}
                m={0}
                justifyContent="center"
                direction="row"
                className="MuiGrid-root-width"
              >
                <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        Filing our 940 online saved us so much time. Very
                        user-friendly.
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>

                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'flex-end',
                        fontWeight: 600,
                      }}
                    >
                      — Colton Briggs
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                      justifyContent: 'space-between',
                      display: 'gird',
                      alignItems: 'center',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        Clear instructions and an easy process. Loved filing
                        Form 940 here.
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 1.5 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'flex-end',
                        fontWeight: 600,
                      }}
                    >
                      — Vanessa
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#f0f2f9',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography
                        component="div"
                        variant="body1"
                        color="gray"
                        sx={{
                          fontSize: { xs: 16, sm: 18, md: 20 },
                          fontFamily: 'san-serif',
                          fontStyle: 'italic',
                          color: '#414042',
                          lineHeight: 1.2,
                        }}
                      >
                        Best experience filing 940—no confusion, just
                        straightforward steps.
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'flex-end',
                        fontWeight: 600,
                      }}
                    >
                      — Camden Voss
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          background: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 1, mb: 4 },
                }}
              >
                Supporting Forms
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6, sm: 6 }}
              sx={{ textAlign: 'left', justifyContent: 'right' }}
            >
              {/* <Link
                            href="/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                            sx={{ textDecoration: 'none' }}
                          > */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 2,
                  textAlign: 'center',
                  alignItems: 'right',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  transition: 'all 0.3s ease 0s',
                  borderRadius: 5,
                  mx: { xs: 0, sm: 2, md: 6 },
                  height: '100%',
                  background: '#fff',
                  // '&:hover': {
                  //   boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  //   transform: 'scale(1.05)',
                  // },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                    textAlign: 'center',
                  }}
                >
                  Form 940 Schedule A
                </Typography>
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Multi-State Employer and Credit Reduction Information -
                  </span>{' '}
                  Report multi-state wage details and credit reduction
                  information required for FUTA reporting.
                </Typography>
              </Paper>
              {/* </Link> */}
            </Grid>
            <Grid
              size={{ xs: 12, md: 6, sm: 6 }}
              sx={{ alignItems: 'left', justifyContent: 'left' }}
            >
              {/* <Link
                href="/resources/employment-tax/form-8974-instructions/"
                sx={{ textDecoration: 'none' }}
              > */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 2,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  background: '#fff',
                  mx: { xs: 0, sm: 2, md: 6 },
                  transition: 'all 0.3s ease 0s',
                  // '&:hover': {
                  //   boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  //   transform: 'scale(1.05)',
                  // },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                    textAlign: 'center',
                  }}
                >
                  Form 940 Amendment
                </Typography>
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}>
                    Adjusted Employer&apos;s Annual Federal Unemployment (FUTA)
                    Tax Return –
                  </span>{' '}
                  Filed to correct errors on previously submitted Form 940.
                </Typography>
              </Paper>
              {/* </Link> */}
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
        component="section"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          E-file Form 940 in just a few minutes using TaxZerone
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Choose a simplified tax filing process. Choose TaxZerone. It&apos;s
          that simple.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form940`}
          >
            E-file Form 940
          </Button>
          <Typography
            variant="body2"
            sx={{ mt: 1, mb: { xs: 0, md: 0 } }}
            color="#dfeae1"
          >
            Takes only a few minutes
          </Typography>
        </Box>
      </Box>
      <Grid
        container
        sx={{
          background: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Grid
            container
            spacing={2}
            m={2}
            direction="row"
            justifyContent="center"
            className="MuiGrid-root-width"
            sx={{ my: { xs: 0, sm: 2 } }}
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  mt: 1,
                  textAlign: 'center',
                }}
              >
                Helpful Resources for IRS Form 940
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4, sm: 6 }}>
              <Link
                href="/resources/employment-tax/form-940-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 0,
                    py: 0,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: 'transparent',
                    borderRadius: 5,
                    height: '100%',
                    backgroundColor: '#f0f2f9',
                    transition: 'all 0.3s ease 0s',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      mb: 0,
                      borderTopRightRadius: 12,
                      borderTopLeftRadius: 12,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/f941-instruction-img.webp" />
                      <source srcSet="/assets/images/f941-instruction-img.png" />
                      <img
                        src="/assets/images/f941-instruction-img.png"
                        alt="Form 943 Instructions"
                        className="partner-card"
                        loading="lazy"
                        width={361}
                        height={292}
                        style={{
                          borderTopRightRadius: 12,
                          borderTopLeftRadius: 12,
                        }}
                      />
                    </picture>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        my: 1,
                        textAlign: 'center',
                      }}
                    >
                      Form 940 Instructions
                    </Typography>
                    <Typography variant="body1">
                      Read the TaxZerone’s Form 940 step-by-step instructions
                      and file accurately with the IRS.
                    </Typography>
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 4, sm: 6 }}>
              <Link
                href="/resources/employment-tax/irs-form-940-due-dates/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: 'transparent',
                    borderRadius: 5,
                    height: '100%',
                    backgroundColor: '#f0f2f9',
                    transition: 'all 0.3s ease 0s',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      mb: 0,
                      borderTopRightRadius: 12,
                      borderTopLeftRadius: 12,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/f941-due-date-img.webp" />
                      <source srcSet="/assets/images/f941-due-date-img.png" />
                      <img
                        src="/assets/images/f941-due-date-img.png"
                        alt="Form 940 Due Dates "
                        className="partner-card"
                        loading="lazy"
                        width={361}
                        height={292}
                        style={{
                          borderTopRightRadius: 12,
                          borderTopLeftRadius: 12,
                        }}
                      />
                    </picture>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        my: 1,
                        textAlign: 'center',
                      }}
                    >
                      Form 940 Due Dates
                    </Typography>
                    <Typography variant="body1">
                      Check out the filing deadlines for Form 940 and stay in
                      compliance with the IRS.
                    </Typography>
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 4, sm: 6 }}>
              <Link
                href="/resources/employment-tax/futa-tax/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: 'transparent',
                    borderRadius: 5,
                    height: '100%',

                    backgroundColor: '#f0f2f9',
                    transition: 'all 0.3s ease 0s',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      mb: 0,
                      borderTopRightRadius: 12,
                      borderTopLeftRadius: 12,
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/f940-futa-img.webp" />
                      <source srcSet="/assets/images/f940-futa-img.png" />
                      <img
                        src="/assets/images/f940-futa-img.png"
                        alt="FUTA "
                        className="partner-card"
                        loading="lazy"
                        width={361}
                        height={292}
                        style={{
                          borderTopRightRadius: 12,
                          borderTopLeftRadius: 12,
                        }}
                      />
                    </picture>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        my: 1,
                        textAlign: 'center',
                      }}
                    >
                      FUTA
                    </Typography>
                    <Typography variant="body1">
                      Learn about FUTA (Federal Unemployment Tax Act), how it
                      applies to employers, and how it connects to your Form 940
                      filing requirements.
                    </Typography>
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          background: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
        component="section"
        aria-labelledby="f940-tax-faqs"
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={2}
            direction="row"
            justifyContent="center"
            className="MuiGrid-root-width"
            sx={{ my: { xs: 0, sm: 5, borderTop: '1px solid #e0e0e0' } }}
          >
            <Grid
              size={{ xs: 12, md: 9, sm: 10 }}
              textAlign="left"
              my={{ xs: 2, sm: 5 }}
              mb={3}
            >
              <Typography
                id="f940-tax-faqs"
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 5,
                  textAlign: 'center',
                }}
              >
                Frequently Asked Questions
              </Typography>

              <Box
                component="div"
                sx={{ mb: 5 }}
                itemScope
                itemType="https://schema.org/FAQPage"
              >
                <Box
                  component="article"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    What is Form 940?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Typography component="p" itemProp="text" sx={{ mt: 1 }}>
                      <Link
                        href="/resources/employment-tax/form-940-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 940
                      </Link>
                      , also known as the Employer&apos;s Annual Federal
                      Unemployment (FUTA) Tax Return, is a tax return used by
                      employers to report their annual federal unemployment tax
                      liabilities. If your business is required to pay FUTA
                      taxes, then you&apos;re required to file Form 940.
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="article"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    Who needs to file Form 940?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box itemProp="text">
                      <Typography component="p" sx={{ mt: 1 }}>
                        Employers subject to paying federal unemployment taxes
                        are required to file Form 940. Generally, if you paid
                        wages of $1,500 or more in any calendar quarter or had
                        at least one employee for some part of a day in any 20
                        or more weeks during the year, you need to file.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  component="article"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    When are Form 940 deposits due?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box component="div" itemProp="text">
                      <Box component="div" sx={{ mb: 1 }}>
                        <TableContainer sx={{ border: '1px solid #828282' }}>
                          <Table sx={{}} aria-label="Due Date Chart">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#ccc' }}>
                                <TableCell
                                  sx={{
                                    color: '#444',
                                    width: 200,
                                    fontSize: 17,
                                  }}
                                >
                                  If your undeposited FUTA tax is more than $500
                                  on...
                                </TableCell>

                                <TableCell
                                  sx={{
                                    color: '#444',
                                    width: 200,
                                    fontSize: 17,
                                  }}
                                >
                                  Deposit your tax by..*
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow sx={{}}>
                                <TableCell>March 31</TableCell>

                                <TableCell sx={{ fontWeight: 600 }}>
                                  April 30
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                <TableCell>June 30</TableCell>

                                <TableCell sx={{ fontWeight: 600 }}>
                                  July 31
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{}}>
                                <TableCell>September 30 </TableCell>

                                <TableCell sx={{ fontWeight: 600 }}>
                                  October 31{' '}
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                <TableCell>December 31</TableCell>

                                <TableCell sx={{ fontWeight: 600 }}>
                                  January 31
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  component="article"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    When is Form 940 due?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Box component="div" itemProp="text">
                      <Typography component="p" sx={{ mt: 1 }}>
                        Form 940 is due by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>{' '}
                        of the following year. If you deposited all FUTA tax on
                        time and in full, you would have an additional{' '}
                        <strong style={{ fontWeight: 600 }}> 10 days</strong>
                        to file Form 940.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  component="article"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.35)',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      color: '#36324a',
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    }}
                    itemProp="name"
                  >
                    Can I file Form 940 electronically?
                  </Typography>

                  <Box
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <Typography component="p" itemProp="text" sx={{ mt: 1 }}>
                      <b> Yes</b>, the IRS encourages employers to file Form 940
                      electronically. TaxZerone also provides a convenient and
                      secure way to electronically file Form 940 with the IRS.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Form940;
